import { SP_MIN_QTY } from '@fsp/contracts';
import { z } from 'zod';
import type { paths } from '@/lib/api/types.gen';

export type ScreenPrintRequestBody = NonNullable<
  paths['/quote/screen-print']['post']['requestBody']
>['content']['application/json'];

/**
 * UI-shaped form schema. Flat so react-hook-form is happy; transformed
 * into the API's nested request body at submit time. `style` and
 * `colorName` are always present in form state but only required when
 * the source is `we_provide_by_style` (superRefine below).
 */
export const UiSchema = z
  .object({
    sxl: z.number().int().min(0),
    xxl: z.number().int().min(0),
    xxxl: z.number().int().min(0),
    // 4XL / 5XL accepted by the schema for forward-compat with the
    // engine's 5-bucket shape. UI doesn't render inputs for these yet
    // (Phase 4b); DEFAULT_VALUES sets them to 0 so react-hook-form's
    // output type stays tight (no `.default()` — that would split
    // z.input from z.output and break the Resolver generic).
    xxxxl: z.number().int().min(0),
    xxxxxl: z.number().int().min(0),

    frontEnabled: z.boolean(),
    frontColors: z.number().int().min(1).max(8),
    backEnabled: z.boolean(),
    backColors: z.number().int().min(1).max(8),
    sleeveEnabled: z.boolean(),
    sleeveColors: z.number().int().min(1).max(8),

    garmentSource: z.enum(['customer_provides', 'we_provide', 'we_provide_by_style']),
    blankCostEach: z.number().min(0),
    garmentColor: z.enum(['white', 'colored']),
    style: z.string(),
    colorName: z.string(),

    mode: z.enum(['b2c', 'b2b']),
    discountPct: z.number().min(0).max(100),
    // Rush surcharge — provisional placeholder values (none / +25% / +50%)
    // pending owner-confirmed multipliers and golden fixture lock. Tunable
    // in the engine's RUSH_MULTIPLIERS table.
    rush: z.enum(['none', 'rush', 'hot_rush']),

    // UI-only — never sent to the engine. Threads through Recents/Pins
    // labels so the owner can identify whose quote a pinned scenario was.
    jobName: z.string().max(80),
  })
  .superRefine((v, ctx) => {
    if (v.garmentSource === 'we_provide_by_style') {
      if (v.style.trim().length === 0) {
        ctx.addIssue({ code: 'custom', path: ['style'], message: 'Style is required' });
      }
      if (v.colorName.trim().length === 0) {
        ctx.addIssue({ code: 'custom', path: ['colorName'], message: 'Color is required' });
      }
    }
  });
export type UiValues = z.output<typeof UiSchema>;

export const DEFAULT_VALUES: UiValues = {
  // Starts at SP_MIN_QTY so the form validates on first load; users raise it.
  sxl: SP_MIN_QTY,
  xxl: 0,
  xxxl: 0,
  xxxxl: 0,
  xxxxxl: 0,
  frontEnabled: true,
  frontColors: 1,
  backEnabled: false,
  backColors: 1,
  sleeveEnabled: false,
  sleeveColors: 1,
  // SS Activewear is the default garment source — most common case per the
  // Phase 4a SS lookup hotfixes (H4/H5) and the Phase 4b dashboard flow.
  garmentSource: 'we_provide_by_style',
  blankCostEach: 0,
  garmentColor: 'colored',
  style: '',
  colorName: '',
  mode: 'b2c',
  discountPct: 0,
  rush: 'none',
  jobName: '',
};

export function toApiInput(v: UiValues): ScreenPrintRequestBody {
  const locations: ScreenPrintRequestBody['locations'] = {};
  if (v.frontEnabled) locations.front = { colors: v.frontColors };
  if (v.backEnabled) locations.back = { colors: v.backColors };
  if (v.sleeveEnabled) locations.sleeve = { colors: v.sleeveColors };
  let garment: ScreenPrintRequestBody['garment'];
  if (v.garmentSource === 'we_provide_by_style') {
    garment = {
      source: 'we_provide_by_style',
      style: v.style.trim(),
      colorName: v.colorName.trim(),
    };
  } else if (v.garmentSource === 'we_provide') {
    garment = {
      source: 'we_provide',
      blankCostEach: v.blankCostEach,
      garmentColor: v.garmentColor,
    };
  } else {
    garment = {
      source: 'customer_provides',
      garmentColor: v.garmentColor,
    };
  }
  return {
    sizes: {
      sxl: v.sxl,
      xxl: v.xxl,
      xxxl: v.xxxl,
      xxxxl: v.xxxxl,
      xxxxxl: v.xxxxxl,
    },
    locations,
    garment,
    mode: v.mode,
    discountPct: v.discountPct,
    rush: v.rush,
  };
}

export type ApiErrorBody = {
  type: string;
  title: string;
  detail: string;
  [k: string]: unknown;
};
export type QuoteError = { status: number; body: ApiErrorBody };

/**
 * Map a QuoteError to human-readable text. Error body shape matches
 * `ApiErrorEnvelopeSchema` in `@fsp/contracts` — see CLAUDE.md § 3.4.
 */
export function formatError(err: QuoteError): { title: string; message: string; detail: string } {
  if (!err || typeof err !== 'object' || !('body' in err) || !err.body) {
    const message =
      err instanceof Error ? err.message : 'The request failed before we could parse the response.';
    return { title: 'Unexpected error', message, detail: message };
  }
  const { type, detail } = err.body;
  if (type === 'network_error') {
    return { title: 'Network error', message: detail, detail };
  }
  if (type === 'not_found') {
    return { title: 'Not found at SS Activewear', message: detail, detail };
  }
  if (type === 'color_not_found') {
    const names = stringArrayOrEmpty(err.body.availableColorNames);
    const families = stringArrayOrEmpty(err.body.availableColorFamilies);
    const suggestion =
      names.length > 0
        ? `Available: ${names.slice(0, 20).join(', ')}${names.length > 20 ? `, and ${names.length - 20} more` : ''}.`
        : families.length > 0
          ? `Color families for this style: ${families.join(', ')}.`
          : '';
    return {
      title: 'Color not available for this style',
      message: suggestion ? `${detail} ${suggestion}` : detail,
      detail,
    };
  }
  if (type === 'upstream_error') {
    return {
      title: 'SS Activewear unavailable',
      message: 'Upstream service returned an error. Try again in a moment.',
      detail,
    };
  }
  if (type === 'service_unavailable') {
    return {
      title: 'Service not configured',
      message: detail,
      detail,
    };
  }
  if (type === 'rate_limited') {
    return {
      title: 'Too many requests',
      message: detail,
      detail,
    };
  }
  if (type === 'below_minimum') {
    const qty = numOrDash(err.body.quantity);
    const min = numOrDash(err.body.minimum);
    return {
      title: 'Quantity below minimum',
      message: `Screen print minimum is ${min} shirts; you entered ${qty}.`,
      detail,
    };
  }
  if (type === 'oversized_blank_cost_required') {
    const size = typeof err.body.size === 'string' ? err.body.size : '4XL/5XL';
    return {
      title: `${size} needs SS Activewear pricing`,
      message: `Switch garment source to "SS Activewear" and pick a style that carries ${size}, or remove the ${size} quantity.`,
      detail,
    };
  }
  if (type === 'below_floor') {
    const computed = currency(err.body.computedPrice);
    const floor = currency(err.body.floor);
    return {
      title: 'Price below floor',
      message: `Computed per-unit sell ${computed} would fall below the floor ${floor}.`,
      detail,
    };
  }
  if (type === 'validation_error' || type === 'invalid_input') {
    return { title: 'Invalid input', message: detail, detail };
  }
  if (type === 'invalid_json') {
    return {
      title: 'Malformed request',
      message: 'The request body was not valid JSON. Check the form inputs and try again.',
      detail,
    };
  }
  if (type === 'route_violation') {
    const suggested =
      typeof err.body.suggestedService === 'string' ? err.body.suggestedService : '';
    const message = suggested
      ? `${detail} Suggested alternative: ${suggested.replace(/_/g, ' ')}.`
      : detail;
    return { title: 'Service not applicable', message, detail };
  }
  if (type === 'missing_auth' || type === 'invalid_token') {
    return {
      title: 'Session expired',
      message: 'Your sign-in expired. Reload the page to continue.',
      detail,
    };
  }
  return { title: err.body.title || 'Error', message: detail, detail };
}

export function currency(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function numOrDash(n: unknown): string {
  return typeof n === 'number' ? String(n) : '—';
}

function stringArrayOrEmpty(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

/**
 * Margin health thresholds. Centralised so the owner can tune without
 * hunting through component code. Used by the result panel's margin bar.
 */
export const MARGIN_THRESHOLDS = {
  poor: 30,
  ok: 50,
} as const;

export function marginTone(pct: number): 'positive' | 'negative' | 'neutral' {
  if (pct >= MARGIN_THRESHOLDS.ok) return 'positive';
  if (pct < MARGIN_THRESHOLDS.poor) return 'negative';
  return 'neutral';
}

export function totalQty(v: Pick<UiValues, 'sxl' | 'xxl' | 'xxxl' | 'xxxxl' | 'xxxxxl'>): number {
  return v.sxl + v.xxl + v.xxxl + v.xxxxl + v.xxxxxl;
}

/**
 * Short human label for a quote — used in the Recent popover rows and
 * the Copy-totals clipboard string. Example outputs:
 *   "Gildan 5000 Black · 72 pcs"
 *   "Customer garment · 48 pcs"
 *   "Manual blank · 50 pcs"
 */
export function quoteLabel(v: UiValues): string {
  const qty = totalQty(v);
  const qtyPart = `${qty} pc${qty === 1 ? '' : 's'}`;
  const job = v.jobName?.trim();
  const prefix = job ? `${job} — ` : '';
  if (v.garmentSource === 'we_provide_by_style') {
    const style = v.style.trim();
    const color = v.colorName.trim();
    if (style && color) return `${prefix}${style} ${color} · ${qtyPart}`;
    if (style) return `${prefix}${style} · ${qtyPart}`;
    return `${prefix}SS Activewear · ${qtyPart}`;
  }
  if (v.garmentSource === 'customer_provides') return `${prefix}Customer garment · ${qtyPart}`;
  return `${prefix}Manual blank · ${qtyPart}`;
}

/**
 * One-liner formatted for clipboard paste (text to a customer, a Slack
 * message, a Google Doc). Keeps garment + qty + per-unit + total +
 * margin in one compact line.
 */
export function clipboardSummary(
  v: UiValues,
  r: {
    perUnitSell: number;
    orderTotal: number;
    marginPct: number;
    mode: 'b2c' | 'b2b';
  },
): string {
  const locations: string[] = [];
  if (v.frontEnabled) locations.push(`Front ${v.frontColors}c`);
  if (v.backEnabled) locations.push(`Back ${v.backColors}c`);
  if (v.sleeveEnabled) locations.push(`Sleeve ${v.sleeveColors}c`);
  const locStr = locations.length > 0 ? locations.join(' + ') : 'No print';
  const base = quoteLabel(v);
  const rushPart = v.rush === 'rush' ? ' · RUSH+25%' : v.rush === 'hot_rush' ? ' · HOT+50%' : '';
  return `${base} · ${locStr}${rushPart} · ${currency(r.perUnitSell)}/ea · ${currency(r.orderTotal)} · ${r.marginPct.toFixed(1)}% margin (${r.mode.toUpperCase()})`;
}
